import Account from "./Account.js";
import AccountStatementItem from "./AccountStatementItem.js";
import Model from "./Model.js";
import Timespan from "./period/Timespan.js";

/**
 * A domain model account statement.
 *
 * @param {account} spec.account - Account for which the account statemens is made
 * @param {number} spec.closingBalance - Value of the closing balance
 * @param {number} spec.closingCreditBalance - Value of the closing credit balance
 * @param {number} spec.closingDebitBalance - Value of the closing debit balance
 * @param {timespan} spec.timespan - Timespan over which the statement spreads
 * @param {number} spec.openingBalance - Value of the opening balance
 * @param {number} spec.openingDebitBalance - Value of the opening debit balance
 * @param {number} spec.openingCreditBalance - Value of the opening credit balance
 * @param {number} spec.transactionChange - Delta between closing balance and opening balance
 * @param {number} spec.transactionCreditChange - Delta between closing credit balance and opening credit balance
 * @param {number} spec.transactionDebitChange - Delta between closing debit balance and opening debit balance
 * @param {accountStatementItem[]} spec.items - list of account statement items
 */
export default class AccountStatement extends Model {
	constructor(spec = {}) {
		super(spec);
		this._account = spec.account;
		this._closingBalance = spec.closingBalance;
		this._closingCreditBalance = spec.closingCreditBalance;
		this._closingDebitBalance = spec.closingDebitBalance;
		this._timespan = spec.timespan;
		this._openingBalance = spec.openingBalance;
		this._openingCreditBalance = spec.openingCreditBalance;
		this._openingDebitBalance = spec.openingDebitBalance;
		this._transactionChange = spec.transactionChange;
		this._transactionCreditChange = spec.transactionCreditChange;
		this._transactionDebitChange = spec.transactionDebitChange;

		this._openingDebit =
			this._openingBalance > 0 ? this._openingBalance : 0;

		this._openingCredit =
			this._openingBalance < 0 ? this._openingBalance : 0;

		this._items = spec.items || [];

		this._sumDebit = this._items.reduce(
			(previousValue, currentTransaction) =>
				previousValue + currentTransaction.getDebit(),
			this._openingDebit,
		);

		this._sumCredit = this._items.reduce(
			(previousValue, currentTransaction) =>
				previousValue + currentTransaction.getCredit(),
			this._openingCredit,
		);
	}

	getItems() {
		return this._items;
	}

	getAccount() {
		return this._account;
	}

	setAccount(newAccount) {
		this._account = newAccount;
	}

	getClosingBalance() {
		return this._closingBalance;
	}

	getClosingCreditBalance() {
		return this._closingCreditBalance;
	}

	getClosingDebitBalance() {
		return this._closingDebitBalance;
	}

	getOpeningDebit() {
		return this._openingDebit;
	}

	getOpeningCredit() {
		return this._openingCredit;
	}

	getSumDebit() {
		return this._sumDebit;
	}

	getSumCredit() {
		return this._sumCredit;
	}

	getOpeningBalance() {
		return this._openingBalance;
	}

	getOpeningCreditBalance() {
		return this._openingCreditBalance;
	}

	getOpeningDebitBalance() {
		return this._openingDebitBalance;
	}

	getTimespan() {
		return this._timespan;
	}

	getTransactionChange() {
		return this._transactionChange;
	}

	getTransactionCreditChange() {
		return this._transactionCreditChange;
	}

	getTransactionDebitChange() {
		return this._transactionDebitChange;
	}

	haveDimensions() {
		return this.getItems().some((item) => item.haveDimensions());
	}

	getReversedOpeningCredit() {
		return this._reverse(this.getOpeningCredit());
	}

	getReversedSumCredit() {
		return this._reverse(this.getSumCredit());
	}

	_reverse(amount) {
		return 0 - amount;
	}

	static transformMaterializationData(data, context) {
		context.setFiscalYear(context.getPeriod().getYear());
		data.timespan &&= Timespan.materialize(data.timespan, context);

		data.account = new Account(data.account);

		data.items = AccountStatementItem.materializeAll(data.items, context);
	}
}
